import React, { useState } from "react";
import { Link } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiBagSuitcase } from "@mdi/js";
import { LandTourCategory, LandTourCompulsoryCharge, LandTourPriceDate } from "../../../../interfaces/land-tour.interface";
import { currencyDisplay } from "../../../../utils/helpers";
import { DateSelection, PaxCounts, pricingPaxCount } from "./ChooseYourTour";
import { usePriceQuote } from "../usePriceQuote";
import CompulsoryChargesModal from "./CompulsoryChargesModal";

const ICONS = "/iceholidays-assets/images/icons";

export interface PriceCardProps {
  landTourId: number | undefined;
  selectedCategory: LandTourCategory | undefined;
  /** The date's window once picked, otherwise the category's headline window. */
  priceWindow: LandTourPriceDate | undefined;
  selectedDate: DateSelection | undefined;
  paxCounts: PaxCounts;
  compulsoryCharges?: LandTourCompulsoryCharge[];
  /** Category, date and headcount are all set: the card shows the Total and Next. */
  complete: boolean;
  total?: number;
  contactHref: string;
  bookingCtaLabel: "Select Tour" | "Next";
  onBookingCta: () => void;
  onShowBreakdown: () => void;
}

export default function GroundTourSidebar({
  landTourId,
  selectedCategory,
  priceWindow,
  selectedDate,
  paxCounts,
  compulsoryCharges = [],
  complete,
  total,
  contactHref,
  bookingCtaLabel,
  onBookingCta,
  onShowBreakdown,
}: PriceCardProps) {
  const [showCompulsory, setShowCompulsory] = useState(false);

  // "Starting from" + the save-amount strikethrough (issue #629 (c)1/2) - the per-adult price
  // for the current category/date/headcount, from the server once calculate_price ships and
  // computed locally until then. See usePriceQuote for the fallback formula.
  const { quote } = usePriceQuote({
    landTourId,
    category: selectedCategory,
    date: selectedDate?.date,
    priceDate: priceWindow,
    quantity: pricingPaxCount(paxCounts),
  });

  return (
    <aside className="ground-tour-show-v2__sidebar">
      {selectedCategory && (
        <div className="ground-tour-show-v2__sidebar-category">
          <span>Tour Category</span>
          <strong>
            <Icon path={mdiBagSuitcase} size="24px" color="#dc1a22" />
            {selectedCategory.category}
          </strong>
        </div>
      )}

      <div className="ground-tour-show-v2__sidebar-footer">
        {complete && total !== undefined ? (
          <div className="ground-tour-show-v2__sidebar-price-block">
            <span className="ground-tour-show-v2__sidebar-total-label">Total</span>
            <div className="ground-tour-show-v2__sidebar-price">
              <span className="ground-tour-show-v2__sidebar-amount">{currencyDisplay("MYR", total)}</span>
            </div>
            <span className="ground-tour-show-v2__sidebar-taxes-note">(incl. taxes & fees)</span>
          </div>
        ) : quote && (
          <div className="ground-tour-show-v2__sidebar-price-block">
            <div className="ground-tour-show-v2__sidebar-price-group">
              <span className="ground-tour-show-v2__sidebar-total-label">Starting from</span>

              {quote.saveAmount > 0 && (
                <div className="ground-tour-show-v2__sidebar-save-row">
                  <span className="ground-tour-show-v2__sidebar-strikethrough">
                    {currencyDisplay("MYR", quote.beforeSaveAmount)}
                  </span>
                  <span className="ground-tour-show-v2__sidebar-save-badge">
                    Save {currencyDisplay("MYR", quote.saveAmount)}
                  </span>
                </div>
              )}

              <div className="ground-tour-show-v2__sidebar-price">
                <span className="ground-tour-show-v2__sidebar-amount">{currencyDisplay("MYR", quote.price)}</span>
                <span className="ground-tour-show-v2__sidebar-suffix">/ person</span>
              </div>
            </div>

            <span className="ground-tour-show-v2__sidebar-taxes-note">(incl. taxes & fees)</span>
          </div>
        )}

        {complete ? (
          <button type="button" className="ground-tour-show-v2__sidebar-breakdown" onClick={onShowBreakdown}>
            Price breakdown
          </button>
        ) : compulsoryCharges.length > 0 && (
          <button
            type="button"
            className="ground-tour-show-v2__sidebar-breakdown"
            onClick={() => setShowCompulsory(true)}
          >
            View Compulsory Charges
          </button>
        )}

        <div className="ground-tour-show-v2__sidebar-actions">
          <button type="button" className="ground-tour-show-v2__sidebar-cta" onClick={onBookingCta}>
            {bookingCtaLabel}
          </button>

          <Link to={contactHref} className="ground-tour-show-v2__sidebar-contact">
            <img src={`${ICONS}/icon-contact-us-red.svg`} alt="" aria-hidden="true" />
            Contact Us
          </Link>
        </div>
      </div>

      {compulsoryCharges.length > 0 && (
        <CompulsoryChargesModal
          open={showCompulsory}
          onClose={() => setShowCompulsory(false)}
          charges={compulsoryCharges}
        />
      )}
    </aside>
  );
}
